import { IPublicClientApplication } from "@azure/msal-browser";

import {
    Configuration,
    MyAccountHttpFunctionsApi,
    DnspDashboardHttpFunctionsApi,
    LookupHttpFunctionsApi,
    DeviceHttpFunctionsApi,
    DeviceOwnerHttpFunctionsApi,
    ChargeTransactionsHttpFunctionsApi,
    TasksHttpFunctionsApi,
    UserAccountHttpFunctionsApi,
    ConsumptionDashboardHttpFunctionsApi,
    GlobalDashboardHttpFunctionsApi,
    PassthroughConfigHttpFunctionsApi,
    DeviceScheduleHttpFunctionsApi,
    DeviceGroupHttpFunctionsApi,
    DeviceFirmwareHttpFunctionsApi,
    NotificationsHttpFunctionsApi,
    WebhookHttpFunctionsApi,
    LogMessagesHttpFunctionsApi,
    SustainabilityDashboardHttpFunctionsApi,
    FaultsHttpFunctionsApi,
    DeviceConnectorPricingHttpFunctionsApi,
    TicketingHttpFunctionsApi,
    NotificationPreferencesHttpFunctionsApi,
    DeviceConfigurationHttpFunctionsApi,
} from "@/api/openapi";
import { BaseAPI } from "@/api/openapi/runtime";
import { IErrorContextType } from "@/contexts/error";
import AuthService from "./auth-service";
import { MessageHeaders } from "@microsoft/signalr";
import { getMessageHeaders } from "@/utils/utils";
import {
    ApiMultipleResultError,
    ApiResultError,
} from "@/api/mutator/custom-fetch";

export class ApiService {
    /**
     * Creates a new API config instance with a valid access token retrieved with MSAL.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns An API config instance with a MSAL access token.
     */
    static async getApiConfigAsync(
        instance: IPublicClientApplication | undefined,
    ): Promise<Configuration> {
        const headers: MessageHeaders = getMessageHeaders();

        if (instance && !import.meta.env["STORYBOOK"]) {
            headers.Authorization =
                await AuthService.getAuthorizationHeader(instance);
        }

        const apiConfig = new Configuration({
            // Read from environment variable if defined (see README.md)
            basePath:
                import.meta.env.VITE_ILLUM_API ??
                "https://illuminatewebdev.jetcharge.com.au/api",
            headers,
            middleware: [
                {
                    post: async ({ response }) => {
                        if (
                            !response.headers
                                .get("content-type")
                                ?.includes("application/json")
                        ) {
                            return response;
                        }
                        const clonedResponse = response.clone();
                        const responseJson = await clonedResponse.json();
                        if (!responseJson.errorNo) return response;
                        if (responseJson.extras)
                            throw new ApiMultipleResultError(responseJson);
                        throw new ApiResultError(responseJson);
                    },
                },
            ],
        });

        return apiConfig;
    }

    /**
     * Creates a new API instance.
     * @param errorContext The error context.
     * @param apiType The fetch API type.
     * @param msalContext The optional MSAL IPublicClientApplication instance.
     * @returns An API instance.
     */
    static async createApiAsync<T extends BaseAPI>(
        errorContext: IErrorContextType | undefined,
        apiType: { new (config: Configuration | undefined): T },
        msalContext: IPublicClientApplication | undefined,
    ): Promise<T> {
        const apiConfig = await ApiService.getApiConfigAsync(msalContext);
        const api = new apiType(apiConfig).withPostMiddleware(
            async (context) => {
                if (
                    !!context.response.status &&
                    errorContext &&
                    errorContext.statusCode !== context.response.status &&
                    context.response.status >= 500
                ) {
                    errorContext.setStatusCode(context.response.status);
                }
            },
        );

        return api;
    }

    /**
     * Get a MyAccountHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured MyAccountHttpFunctionsApi instance.
     */
    static async createMyAccountApiAsync(
        errorContext: IErrorContextType | undefined,
        msalContext: IPublicClientApplication | undefined = undefined,
    ): Promise<MyAccountHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            MyAccountHttpFunctionsApi,
            msalContext,
        );
    }

    /**
     * Get a DnspDashboardHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured DnspDashboardHttpFunctionsApi instance.
     */
    static async createDnspDashboardApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<DnspDashboardHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DnspDashboardHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a LookupHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createLookupApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<LookupHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            LookupHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a NotificationsHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createNotificationsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<NotificationsHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            NotificationsHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a DeviceConfigurationHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured DeviceConfigurationHttpFunctionsApi instance.
     */
    static async createDeviceConfigurationApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<DeviceConfigurationHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceConfigurationHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a DeviceHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured DeviceHttpFunctionsApi instance.
     */
    static async createDeviceApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<DeviceHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a DeviceOwnerHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured DeviceOwnerHttpFunctionsApi instance.
     */
    static async createDeviceOwnerApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<DeviceOwnerHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceOwnerHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a ChargeTransactionsHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured ChargeTransactionsHttpFunctionsApi instance.
     */
    static async createChargeTransactionsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<ChargeTransactionsHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            ChargeTransactionsHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a TasksHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured TasksHttpFunctionsApi instance.
     */
    static async createTasksApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<TasksHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            TasksHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a UserAccountHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured UserAccountHttpFunctionsApi instance.
     */
    static async createUserAccountApiAsync(
        errorContext: IErrorContextType | undefined,
        instance: IPublicClientApplication,
    ): Promise<UserAccountHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            UserAccountHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a ConsumptionDashboardHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured ConsumptionDashboardHttpFunctionsApi instance.
     */
    static async createConsumptionDashboardHttpFunctionsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<ConsumptionDashboardHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            ConsumptionDashboardHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a GlobalDashboardHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param msalContext The MSAL IPublicClientApplication instance.
     * @returns A configured GlobalDashboardHttpFunctionsApi instance.
     */
    static async createGlobalDashboardHttpFunctionsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ): Promise<GlobalDashboardHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            GlobalDashboardHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a PassthroughConfigHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createPassthroughConfigApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<PassthroughConfigHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            PassthroughConfigHttpFunctionsApi,
            instance,
        );
    }
    /**
     * Get a DeviceScheduleHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createDeviceScheduleApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<DeviceScheduleHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceScheduleHttpFunctionsApi,
            instance,
        );
    }
    static async createDeviceGroupApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<DeviceGroupHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceGroupHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a DeviceFirmwareHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured DeviceFirmwareHttpFunctionsApi instance.
     */
    static async createDeviceFirmwareApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<DeviceFirmwareHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceFirmwareHttpFunctionsApi,
            instance,
        );
    }
    /**
     * Get a WebhookApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured WebhookApi instance.
     */
    static async createWebhookApiApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<WebhookHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            WebhookHttpFunctionsApi,
            instance,
        );
    }
    /**
     * Get a LogMessagesHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured LogMessagesHttpFunctionsApi instance.
     */
    static async createLogMessagesFunctionsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<LogMessagesHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            LogMessagesHttpFunctionsApi,
            instance,
        );
    }
    /**
     * Get a SustainabilityDashboardHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createSustainabilityDashboardApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<SustainabilityDashboardHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            SustainabilityDashboardHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a FaultsHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createFaultsApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<FaultsHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            FaultsHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a DeviceConnectorPricingHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createDeviceConnectorPricingApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<DeviceConnectorPricingHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            DeviceConnectorPricingHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a TicketingHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createTicketingApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<TicketingHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            TicketingHttpFunctionsApi,
            instance,
        );
    }

    /**
     * Get a NotificationPreferencesHttpFunctionsApi instance.
     * @param errorContext The error context.
     * @param instance The optional MSAL IPublicClientApplication instance.
     * @returns A configured IPublicClientApplication instance.
     */
    static async createNotificationPreferencesApiAsync(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication | undefined = undefined,
    ): Promise<NotificationPreferencesHttpFunctionsApi> {
        return await ApiService.createApiAsync(
            errorContext,
            NotificationPreferencesHttpFunctionsApi,
            instance,
        );
    }
}

export const createMyAccountApiAsync = ApiService.createMyAccountApiAsync;

export const createDnspDashboardApiAsync =
    ApiService.createDnspDashboardApiAsync;
